import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useToast } from '../components/ui/ToastManager';
import { ROUTES } from '../config/routes';
import { registerFormStyles, createDynamicStyles } from '../styles/components/RegisterForm.styles';
import styles, { hoverEffects, styleUtils } from '../styles/components/CreditCardEditForm.styles';
import { Eye, EyeOff } from 'lucide-react';
import {
  validatePassword,
  checkPasswordStrength,
  isPasswordValid,
  VALIDATION_MESSAGES
} from '../utils/formValidation';

/**
 * パスワード変更フォームページ
 * クレジットカード変更画面と同じスタイルに統一
 */
const PasswordChangeForm = () => {
  const navigate = useNavigate();
  const { currentUser, getCsrfTokenFromCookie, logout } = useAuth();
  const { showSuccess, showError } = useToast();

  const [formData, setFormData] = useState({
    currentPassword: '',
    newPassword: '',
    newPasswordConfirm: ''
  });

  const [errors, setErrors] = useState({});
  const [passwordStrength, setPasswordStrength] = useState({
    length: false,
    uppercase: false,
    lowercase: false,
    number: false
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  // パスワード表示・非表示の状態管理（各フィールド独立）
  const [passwordVisibility, setPasswordVisibility] = useState({
    current: false,
    new: false,
    confirm: false
  });

  // パスワード表示切替ハンドラー
  const togglePasswordVisibility = (field) => {
    setPasswordVisibility(prev => ({
      ...prev,
      [field]: !prev[field]
    }));
  };

  /**
   * 入力変更ハンドラー
   */
  const handleChange = (e) => {
    const { name, value } = e.target;
    
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));

    // リアルタイムバリデーション
    if (name === 'currentPassword' && errors.currentPassword) {
      setErrors(prev => ({
        ...prev,
        currentPassword: ''
      }));
    }

    if (name === 'newPassword') {
    // 新しいパスワードの強度チェック
    const strength = checkPasswordStrength(value);
    setPasswordStrength(strength);

    if (errors.newPassword) {
      setErrors(prev => ({
        ...prev,
        newPassword: ''
      }));
    }

    // 確認用パスワードが入力済みの場合、一致チェック
    if (!formData.newPasswordConfirm) return;
    
    const matchError = value !== formData.newPasswordConfirm ? 
      VALIDATION_MESSAGES.PASSWORD_MISMATCH : '';
    setErrors(prev => ({
      ...prev,
      newPasswordConfirm: matchError
    }));
  }

    if (name === 'newPasswordConfirm') {
      const matchError = formData.newPassword !== value && value !== '' ? 
        VALIDATION_MESSAGES.PASSWORD_MISMATCH : '';
      setErrors(prev => ({
        ...prev,
        newPasswordConfirm: matchError
      }));
    }
  };

  /**
   * 現在のパスワードのバリデーション
   */
  const validateCurrentPassword = (value) => {
    if (!value) {
      return '現在のパスワードを入力してください';
    }
    return '';
  };

  /**
   * 新しいパスワードのバリデーション
   */
  const validateNewPassword = (value, currentPassword) => {
    if (!value) {
      return '新しいパスワードを入力してください';
    }
    
    const passwordError = validatePassword(value, {
      minLength: 8,
      requireStrong: true
    });
    if (passwordError) {
      return passwordError;
    }
    
    if (currentPassword && value === currentPassword) {
      return '現在のパスワードと同じパスワードは使用できません';
    }
    
    return '';
  };

  /**
   * パスワード確認のバリデーション
   */
  const validateNewPasswordConfirm = (value, newPassword) => {
    if (!value) {
      return 'パスワード確認を入力してください';
    }
    
    if (value !== newPassword) {
      return VALIDATION_MESSAGES.PASSWORD_MISMATCH;
    }
    
    return '';
  };

  /**
   * バリデーション
   */
  const validate = () => {
    const newErrors = {};

    const currentPasswordError = validateCurrentPassword(formData.currentPassword);
    if (currentPasswordError) {
      newErrors.currentPassword = currentPasswordError;
    }

    const newPasswordError = validateNewPassword(
      formData.newPassword, 
      formData.currentPassword
    );
    if (newPasswordError) {
      newErrors.newPassword = newPasswordError;
    }

    const newPasswordConfirmError = validateNewPasswordConfirm(
      formData.newPasswordConfirm,
      formData.newPassword
    );
    if (newPasswordConfirmError) {
      newErrors.newPasswordConfirm = newPasswordConfirmError;
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  /**
   * フォーム送信処理
   */
  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validate()) {
      showError('入力内容をご確認ください');
      return;
    }

    setIsSubmitting(true);

    try {

      const csrfToken = await getCsrfTokenFromCookie(true);
      
      if (!csrfToken) {
        throw new Error('セキュリティトークンの取得に失敗しました');
      }

      const response = await fetch('/api/me/password', {
        method: 'PUT',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-TOKEN': csrfToken
        },
        body: JSON.stringify({
          currentPassword: formData.currentPassword,
          newPassword: formData.newPassword,
          newPasswordConfirm: formData.newPasswordConfirm
        })
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.message || 'パスワードの変更に失敗しました');
      }

      showSuccess('パスワードを変更しました');
      await logout();
      
      navigate(ROUTES.LOGIN, {
        state: { message: 'パスワードを変更しました。新しいパスワードで再度ログインしてください。' }
      });

    } catch (error) {
      console.error('[PASSWORD CHANGE FORM] Error:', error);
      showError(error.message || 'パスワード変更中にエラーが発生しました');
    } finally {
      setIsSubmitting(false);
    }
  };

  /**
   * 戻るボタンの処理
   */
  const handleBack = () => {
    navigate(ROUTES.HOME);
  };

  /**
   * パスワード強度インジケーターをレンダリング
   */
  const renderPasswordStrengthIndicator = () => {
    if (formData.newPassword.length === 0) return null;

    const requirements = [
      { key: 'length', label: '8文字以上', met: passwordStrength.length },
      { key: 'uppercase', label: '英大文字を含む (A-Z)', met: passwordStrength.uppercase },
      { key: 'lowercase', label: '英小文字を含む (a-z)', met: passwordStrength.lowercase },
      { key: 'number', label: '数字を含む (0-9)', met: passwordStrength.number }
    ];

    return (
      <div style={{ marginTop: '10px' }}>
        <div style={{ fontSize: '14px', fontWeight: 'bold', marginBottom: '8px', color: '#333' }}>
          パスワード要件:
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
          {requirements.map(({ key, label, met }) => (
            <div key={key} style={{
              display: 'flex', 
              alignItems: 'center', 
              fontSize: '13px',
              color: met ? '#28a745' : '#666'
            }}>
              <span style={{
                marginRight: '8px', 
                fontSize: '16px',
                color: met ? '#28a745' : '#dc3545'
              }}>
                {met ? '✓' : '✗'}
              </span>
              <span>{label}</span>
            </div>
          ))}
        </div>

        {isPasswordValid(passwordStrength) && (
          <div style={{
            marginTop: '8px', 
            padding: '8px 12px',
            backgroundColor: '#d4edda', 
            border: '1px solid #c3e6cb',
            borderRadius: '4px', 
            color: '#155724',
            fontSize: '13px', 
            fontWeight: 'bold'
          }}>
            ✓ パスワード要件をすべて満たしています
          </div>
        )}
      </div>
    );
  };

  /**
   * パスワード入力フィールドのレンダリング
   */
  const renderPasswordField = (name, label, placeholder, visibilityKey) => {
    const passwordInputContainerStyle = {
      position: 'relative',
      display: 'flex',
      alignItems: 'center'
    };

    const passwordToggleButtonStyle = {
      position: 'absolute',
      right: '10px',
      background: 'none',
      border: 'none',
      cursor: 'pointer',
      padding: '5px',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      color: '#666',
      transition: 'color 0.2s'
    };

    return (
      <tr key={name} className="password-change-table-row">
        <th style={registerFormStyles.th} className="password-change-table-th">
          <span>{label}</span>
          <span style={registerFormStyles.required}>*</span>
        </th>
        <td style={registerFormStyles.td} className="password-change-table-td">
          <div style={passwordInputContainerStyle}>
            <input
              type={passwordVisibility[visibilityKey] ? 'text' : 'password'}
              name={name}
              value={formData[name]}
              onChange={handleChange}
              style={{
                ...createDynamicStyles.getInputStyle(!!errors[name]),
                paddingRight: '40px'
              }}
              disabled={isSubmitting}
              autoComplete={name === 'currentPassword' ? 'current-password' : 'new-password'}
              placeholder={placeholder}
            />
            <button
              type="button"
              onClick={() => togglePasswordVisibility(visibilityKey)}
              style={passwordToggleButtonStyle}
              disabled={isSubmitting}
              aria-label={passwordVisibility[visibilityKey] ? 'パスワードを非表示' : 'パスワードを表示'}
            >
              {passwordVisibility[visibilityKey] ? <EyeOff size={18} /> : <Eye size={18} />}
            </button>
          </div>
          {errors[name] && (
            <div style={createDynamicStyles.getMessageStyle('error')}>{errors[name]}</div>
          )}
          
          {/* 新しいパスワードの場合は強度インジケーター表示 */}
          {name === 'newPassword' && renderPasswordStrengthIndicator()}
          
          {/* パスワード確認の一致表示 */}
          {name === 'newPasswordConfirm' && 
           formData.newPasswordConfirm && 
           formData.newPassword === formData.newPasswordConfirm && 
           !errors.newPasswordConfirm && (
            <div style={{
              color: '#28a745',
              fontSize: '13px',
              marginTop: '8px',
              fontWeight: '500'
            }}>
              ✓ パスワードが一致しています
            </div>
          )}
        </td>
      </tr>
    );
  };

  return (
    <div style={styles.container} className="password-change-container">
      <div style={styles.content} className="password-change-content">
        <div style={styles.header} className="password-change-header">
          <h1 style={styles.title} className="password-change-title">パスワード変更</h1>
          <button
            type="button"
            style={styles.backButton}
            className="password-change-back-button"
            onMouseOver={(e) => hoverEffects.applyBackButtonHover(e.target)}
            onMouseOut={(e) => hoverEffects.resetBackButtonHover(e.target)}
            onClick={handleBack}
            disabled={isSubmitting}
          >
            ← ホームに戻る
          </button>
        </div>
        
        <div style={styles.notice} className="password-change-notice">
          <span>セキュリティのため、現在のパスワードと新しいパスワードを入力してください。</span><br />
          <span>パスワード変更後は、新しいパスワードで再度ログインが必要です。</span>
        </div>

        {currentUser && (
          <div style={{
            backgroundColor: '#f8f9fa',
            padding: '12px 16px',
            borderRadius: '4px',
            marginBottom: '20px',
            display: 'flex',
            alignItems: 'center',
            gap: '10px'
          }} className="password-user-info">
            <span style={{ fontSize: '14px', color: '#666', fontWeight: 'bold' }}>
              ログイン中:
            </span>
            <span style={{ fontSize: '14px', color: '#09529f', fontWeight: 'bold' }}>
              {currentUser.email}
            </span>
          </div>
        )}
        
        {/* メインフォーム */}
        <form onSubmit={handleSubmit} noValidate>
          <h2 style={registerFormStyles.pageTitle}>パスワード変更情報</h2>
          
          {/* パスワード入力テーブル */}
          <table style={registerFormStyles.table} className="password-change-table">
            <tbody>
              {renderPasswordField(
                'currentPassword',
                '現在のパスワード',
                '現在のパスワードを入力',
                'current'
              )}
              
              {renderPasswordField(
                'newPassword',
                '新しいパスワード',
                '新しいパスワードを入力',
                'new'
              )}
              
              {renderPasswordField(
                'newPasswordConfirm',
                '新しいパスワード（確認）',
                '確認のため再入力',
                'confirm'
              )}
            </tbody>
          </table>

          {/* セキュリティに関する注意事項 */}
          <div style={{
            backgroundColor: '#f8f9fa',
            border: '1px solid #dee2e6',
            borderRadius: '4px',
            padding: '16px',
            marginBottom: '20px'
          }} className="password-security-note">
            <div style={{
              fontSize: '14px',
              fontWeight: 'bold',
              marginBottom: '12px',
              color: '#09529f'
            }}>
              セキュリティに関するご注意
            </div>
            <ul style={{
              margin: 0,
              paddingLeft: '20px',
              fontSize: '13px',
              color: '#666',
              lineHeight: '1.8'
            }}>
              <li>パスワードは定期的に変更することを推奨します</li>
              <li>他のサービスと同じパスワードは使用しないでください</li>
              <li>パスワードは第三者に教えないでください</li>
            </ul>
          </div>

          {/* 送信ボタンエリア */}
          <div style={styles.buttonContainer} className="password-change-button-container">
            <button
              type="button"
              style={styleUtils.getButtonStyle('secondary')}
              className="password-change-button"
              onMouseOver={(e) => hoverEffects.applySecondaryButtonHover(e.target)}
              onMouseOut={(e) => hoverEffects.resetSecondaryButtonHover(e.target)}
              onClick={handleBack}
              disabled={isSubmitting}
            >
              キャンセル
            </button>
            <button
              type="submit"
              style={{
                ...styleUtils.getButtonStyle('primary', isSubmitting),
                backgroundColor: '#09529f'
              }}
              className="password-change-button"
              onMouseOver={(e) => hoverEffects.applyPrimaryButtonHover(e.target, isSubmitting)}
              onMouseOut={(e) => hoverEffects.resetPrimaryButtonHover(e.target, isSubmitting)}
              disabled={isSubmitting}
            >
              {isSubmitting ? '変更中...' : '更新'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default PasswordChangeForm;